import {
  useIdentifierFrageFinder,
  useIdentifierFragenblockFinder,
} from '@/composables/Bestandsaufnahme/useIdentifierFinder';
import Bestandsaufnahme from '@/models/ba/Bestandsaufnahme';
import { Fragenblock } from '@/models/ba/Fragenblock';

function getAuswahlWert(ba: Bestandsaufnahme, auswahlFragesIdentifier: string) {
  return useIdentifierFrageFinder(ba, auswahlFragesIdentifier)?.[
    'eingabeAuswahlWert'
  ];
}

function get1NModalAuswahlWert(
  ba: Bestandsaufnahme,
  modalIdentifier: string,
  auswahlFragesIdentifier: string
) {
  const modal = useIdentifierFragenblockFinder(ba, modalIdentifier);

  const modale = modal?.fragenblocks?.filter(
    (block) => !block.config.isTemplate
  );

  return modale
    ?.map((modal) => {
      return modal.fragenblocks.map((fragenBlock) => {
        return useIdentifierFrageFinder(
          ba,
          fragenBlock.path + '.' + auswahlFragesIdentifier
        )?.['eingabeAuswahlWert'];
      });
    })
    .flat(1);
}

export function useGesamtflaechen() {
  const summarize = (bewertungen: Array<string | undefined>) => {
    // Priority what comes first
    if (bewertungen.includes('x')) {
      return {
        color: "red",
        amount: bewertungen.filter((f) => f === 'x').length.toString(),
      };
    } else if (bewertungen.includes('minus')) {
      return {
        color: "yellow",
        amount: bewertungen.filter((f) => f === 'minus').length.toString(),
      };
    } else if (bewertungen.includes('o')) {
      return {
        color: "blue",
        amount: bewertungen.filter((f) => f === 'o').length.toString(),
      };
    } else if (bewertungen.includes('plus')) {
      return {
        color: "green",
        amount: bewertungen.filter((f) => f === 'plus').length.toString(),
      };
    }
    //default value, to see something does not work
    return { color: "0", amount: '0' };
  };
  const getArbeitsFlaecheBewertungen = (ba: Bestandsaufnahme) => {
    return {
      allgemein: {
        'nutzungsart-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.allgemeine-punkte-group.nutzungsart-bewertung'
        ),
        'fremdobjekte-auf-flaeche-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.allgemeine-punkte-group.fremdobjekte-auf-flaeche-bewertung'
        )
      },
      gelaende: {
        'gefaelle-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.gefaelle-group.gefaelle-bewertung'
        ),
        'ausholzung-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.ausholzung-group.ausholzung-bewertung'
        ),
        'rodung-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.rodung-group.rodung-bewertung'
        ),
        'graben-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.graben-group.graben-bewertung'
        ),
        'feuchtgebiete-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.feuchtgebiete-group.feuchtgebiete-bewertung'
        ),
        'naturdenkmaeler-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.naturdenkmaeler-group.naturdenkmaeler-bewertung'
        ),
        'kampfmittel-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.kampfmittel-group.kampfmittel-bewertung'
        ),
        'sonstiges-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.sonstiges-group.sonstiges-bewertung'
        ),
      },
      oekologie: {
        'biotoptypkartierung-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.biotoptypkartierung-bewertung'
        ),
        'einzelbaeume-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.einzelbaeume-bewertung'
        ),
        'archaeologisches-denkmal-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.archaeologisches-denkmal-bewertung'
        ),
        'ffh-gebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.ffh-gebiet-bewertung'
        ),
        'vogelschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.vogelschutzgebiet-bewertung'
        ),
        'geotop-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.geotop-bewertung'
        ),
        'geschuetztes-offenlandbiotop-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.geschuetztes-offenlandbiotop-bewertung'
        ),
        'geschuetztes-waldbiotop-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.geschuetztes-waldbiotop-bewertung'
        ),
        'naturdenkmal-flaechenhaft-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.naturdenkmal-flaechenhaft-bewertung'
        ),
        'naturdenkmal-einzelgebilde-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.naturdenkmal-einzelgebilde-bewertung'
        ),
        'ueberschwemmungsgebiet-hq100-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.ueberschwemmungsgebiet-hq100-bewertung'
        ),
        'ueberschwemmungsgebiet-m1-m2-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.ueberschwemmungsgebiet-m1-m2-bewertung'
        ),
        'gewaessernetz-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.gewaessernetz-bewertung'
        ),
        'wasserschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.wasserschutzgebiet-bewertung'
        ),
         'heilquellenschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.heilquellenschutzgebiet-bewertung'
        ),
         'landschaftsschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.landschaftsschutzgebiet-bewertung'
        ),
         'waldschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.waldschutzgebiet-bewertung'
        ),
         'naturschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.naturschutzgebiet-bewertung'
        ),
         'biosphaerengebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.biosphaerengebiet-bewertung'
        ),
         'bodendenkmale-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.bodendenkmale-bewertung'
        ),
         'hoehlenbaeume-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.hoehlenbaeume-bewertung'
        ),
         'haselmausnester-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.haselmausnester-bewertung'
        ),
         'reptilien-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.arbeitsflaeche-mast-tab.oekologie-group.reptilien-bewertung'
        ),
      },
    };
  };
  const getZuwegungBewertungen = (ba: Bestandsaufnahme) => {
    return {
      'beschaffenheit-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.beschaffenheit.beschaffenheit-bewertung'
      ),
      'gefaelle-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.gefaelle.gefaelle-bewertung'
      ),
      'ausholzung-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.ausholzung.ausholzung-bewertung'
      ),
      'rodung-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.rodung.rodung-bewertung'
      ),
      'zuwegung-breite-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.zuwegung.zuwegung-breite-bewertung'
      ),
      'beschraenkung-hoehe-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.beschraenkung.beschraenkung-hoehe-bewertung'
      ),
      'beschraenkung-gewicht-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.beschraenkung.beschraenkung-gewicht-bewertung'
      ),
      'ringverkehr-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.ringverkehr.ringverkehr-bewertung'
      ),
      'ausweichflaechen-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.ausweichflaechen.ausweichflaechen-bewertung'
      ),
      'alternative-zufahrt-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.alternative-zufahrt.alternative-zufahrt-bewertung'
      ),
      'sonstiges-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.sonstiges.sonstiges-bewertung'
      ),
      'graben-bewertung': get1NModalAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.graben',
        'graben-bewertung'
      ),
      'bauwerk-bewertung': get1NModalAuswahlWert(
        ba,
        'tng-be-flaechen.zuwegung-mast-tab.bauwerke',
        'bauwerk-bewertung'
      ),
   'biotoptypkartierung-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.biotoptypkartierung-bewertung'
        ),
        'einzelbaeume-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.einzelbaeume-bewertung'
        ),
        'archaeologisches-denkmal-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.archaeologisches-denkmal-bewertung'
        ),
        'ffh-gebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.ffh-gebiet-bewertung'
        ),
        'vogelschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.vogelschutzgebiet-bewertung'
        ),
        'geotop-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.geotop-bewertung'
        ),
        'geschuetztes-offenlandbiotop-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.geschuetztes-offenlandbiotop-bewertung'
        ),
        'geschuetztes-waldbiotop-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.geschuetztes-waldbiotop-bewertung'
        ),
        'naturdenkmal-flaechenhaft-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.naturdenkmal-flaechenhaft-bewertung'
        ),
        'naturdenkmal-einzelgebilde-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.naturdenkmal-einzelgebilde-bewertung'
        ),
        'ueberschwemmungsgebiet-hq100-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.ueberschwemmungsgebiet-hq100-bewertung'
        ),
        'ueberschwemmungsgebiet-m1-m2-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.ueberschwemmungsgebiet-m1-m2-bewertung'
        ),
        'gewaessernetz-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.gewaessernetz-bewertung'
        ),
        'wasserschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.wasserschutzgebiet-bewertung'
        ),
         'heilquellenschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.heilquellenschutzgebiet-bewertung'
        ),
         'landschaftsschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.landschaftsschutzgebiet-bewertung'
        ),
         'waldschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.waldschutzgebiet-bewertung'
        ),
         'naturschutzgebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.naturschutzgebiet-bewertung'
        ),
         'biosphaerengebiet-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.biosphaerengebiet-bewertung'
        ),
         'bodendenkmale-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.bodendenkmale-bewertung'
        ),
         'hoehlenbaeume-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.hoehlenbaeume-bewertung'
        ),
         'haselmausnester-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.haselmausnester-bewertung'
        ),
         'reptilien-bewertung': getAuswahlWert(
          ba,
          'tng-be-flaechen.zuwegung-mast-tab.oekologie-group.reptilien-bewertung'
        ),
    };
  };
  const getFremdleitungenBewertungen = (ba: Bestandsaufnahme) => {
    return {
      'fremdleitung-bewertung': get1NModalAuswahlWert(
        ba,
        'tng-be-flaechen.fremdleitungen-tab.fremdleitungen-group',
        'fremdleitung-bewertung'
      ),
      'sonstiges-bewertung': getAuswahlWert(
        ba,
        'tng-be-flaechen.fremdleitungen-tab.fremdleitungen-group.sonstiges-bewertung'
      ),
    };
  };
  const getSchutzgeruesteBewertungen = (ba: Bestandsaufnahme) => {
    const schutzgerueste = getSchutzgerueste(ba); 
    let alleBewertungen: any[] = [];
  
    schutzgerueste?.forEach((schutzgeruest) => {
      const bewertungen = [
        schutzgeruest.findFrageByRelativePath(ba, 'schuetzendes-objekt-bewertung')?.['eingabeAuswahlWert'],
        schutzgeruest.findFrageByRelativePath(ba, 'laenge-schutzgeruest-bewertung')?.['eingabeAuswahlWert'],
        schutzgeruest.findFrageByRelativePath(ba, 'breite-geruestwaende-bewertung')?.['eingabeAuswahlWert'],
        schutzgeruest.findFrageByRelativePath(ba, 'hoehe-geruestwaende-bewertung')?.['eingabeAuswahlWert'],
        schutzgeruest.findFrageByRelativePath(ba, 'spannweite-netz-bewertung')?.['eingabeAuswahlWert'],
        schutzgeruest.findFrageByRelativePath(ba, 'entfernung-hoehe-geruestwaende-bewertung')?.['eingabeAuswahlWert'],
        schutzgeruest.findFrageByRelativePath(ba, 'abstand-freileitung-bewertung')?.['eingabeAuswahlWert']
      ];
        alleBewertungen = alleBewertungen.concat(bewertungen); 
    });
    return alleBewertungen; 
  };
  const getSchutzgeruestBewertungen = (ba: Bestandsaufnahme, fragenblock: Fragenblock) => {
    return {
      'schuetzendes-objekt-bewertung': fragenblock.findFrageByRelativePath(ba,'schuetzendes-objekt-bewertung')?.['eingabeAuswahlWert'],
      'laenge-schutzgeruest-bewertung': fragenblock.findFrageByRelativePath(ba,'laenge-schutzgeruest-bewertung')?.['eingabeAuswahlWert'],
      'breite-geruestwaende-bewertung': fragenblock.findFrageByRelativePath(ba,'breite-geruestwaende-bewertung')?.['eingabeAuswahlWert'],
      'hoehe-geruestwaende-bewertung': fragenblock.findFrageByRelativePath(ba,'hoehe-geruestwaende-bewertung')?.['eingabeAuswahlWert'],
      'spannweite-netz-bewertung': fragenblock.findFrageByRelativePath(ba,'spannweite-netz-bewertung')?.['eingabeAuswahlWert'],
      'entfernung-hoehe-geruestwaende-bewertung': fragenblock.findFrageByRelativePath(ba,'entfernung-hoehe-geruestwaende-bewertung')?.['eingabeAuswahlWert'],
      'abstand-freileitung-bewertung': fragenblock.findFrageByRelativePath(ba,'abstand-freileitung-bewertung')?.['eingabeAuswahlWert']
    };
  };
  const summarizeSchutzgeruesteBewertung = (ba: Bestandsaufnahme) => {
    return summarize( getSchutzgeruesteBewertungen(ba) );
  };
  const summarizeSeilzugflaecheBewertungen = (ba: Bestandsaufnahme, fragenblock: Fragenblock) => {
    let bewertungen: Array<string | undefined> = Object.values(
      getSeilzugflaecheBewertungen(ba, fragenblock)
    )
      .flat(1)
      .map((f) => (typeof f === "object" && f !== null ? Object.values(f) : f))
      .flat(1) as Array<string | undefined>;
    
    const bewertungenGraben: any = Object.values(
      getGrabenquerungenBewertungen(ba, fragenblock) as any
    )
      .flat(1)
      .map((f) => Object.values(f as Record<string, unknown>))
      .flat(1);

    bewertungen = bewertungen.concat(bewertungenGraben);

    return summarize(bewertungen);
  };
  const getSeilzugflaecheBewertungen = (ba: Bestandsaufnahme, fragenblock: Fragenblock) => {
    return {
      seilzugflaeche: {
        //Algemein
        'entfernung-mast-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.allgemein.entfernung-mast-bewertung')?.['eingabeAuswahlWert'],
        'nutzungsart-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.allgemein.nutzungsart-bewertung')?.['eingabeAuswahlWert'],
        'fremdobjekte-auf-flaeche-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.allgemein.fremdobjekte-auf-flaeche-bewertung')?.['eingabeAuswahlWert'],
        //Gelände
        'gefaelle-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.gefaelle-group.gefaelle-bewertung')?.['eingabeAuswahlWert'],
        'ausholzung-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.ausholzung-group.ausholzung-bewertung')?.['eingabeAuswahlWert'],
        'rodung-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.rodung-group.rodung-bewertung')?.['eingabeAuswahlWert'],
        'graben-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.graben-group.graben-bewertung')?.['eingabeAuswahlWert'],
        'feuchtgebiete-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.feuchtgebiete-group.feuchtgebiete-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmaeler-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.naturdenkmaeler-group.naturdenkmaeler-bewertung')?.['eingabeAuswahlWert'],
        'kampfmittel-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.kampfmittel-group.kampfmittel-bewertung')?.['eingabeAuswahlWert'],
        'sonstiges-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.sonstiges-group.sonstiges-bewertung')?.['eingabeAuswahlWert'],
        //Ökologie
        'biotoptypkartierung-bewertung':fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.biotoptypkartierung-bewertung')?.['eingabeAuswahlWert'],
        'einzelbaeume-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.einzelbaeume-bewertung')?.['eingabeAuswahlWert'],
        'archaeologisches-denkmal-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.archaeologisches-denkmal-bewertung')?.['eingabeAuswahlWert'],
        'ffh-gebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.ffh-gebiet-bewertung')?.['eingabeAuswahlWert'],
        'vogelschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.vogelschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'geotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.geotop-bewertung')?.['eingabeAuswahlWert'],
        'geschuetztes-offenlandbiotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.geschuetztes-offenlandbiotop-bewertung')?.['eingabeAuswahlWert'],
        'geschuetztes-waldbiotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.geschuetztes-waldbiotop-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmal-flaechenhaft-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.naturdenkmal-flaechenhaft-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmal-einzelgebilde-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.naturdenkmal-einzelgebilde-bewertung')?.['eingabeAuswahlWert'],
        'ueberschwemmungsgebiet-hq100-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.ueberschwemmungsgebiet-hq100-bewertung')?.['eingabeAuswahlWert'],
        'ueberschwemmungsgebiet-m1-m2-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.ueberschwemmungsgebiet-m1-m2-bewertung')?.['eingabeAuswahlWert'],
        'gewaessernetz-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.gewaessernetz-bewertung')?.['eingabeAuswahlWert'],
        'wasserschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.wasserschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'heilquellenschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.heilquellenschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'landschaftsschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.landschaftsschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'waldschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.waldschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'naturschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.naturschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'biosphaerengebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.biosphaerengebiet-bewertung')?.['eingabeAuswahlWert'],
        'bodendenkmale-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.bodendenkmale-bewertung')?.['eingabeAuswahlWert'],
        'hoehlenbaeume-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.hoehlenbaeume-bewertung')?.['eingabeAuswahlWert'],
        'haselmausnester-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.haselmausnester-bewertung')?.['eingabeAuswahlWert'],
        'reptilien-bewertung': fragenblock.findFrageByRelativePath(ba, 'seilzugflaeche-modal.oekologie-group.reptilien-bewertung')?.['eingabeAuswahlWert'],
      },
      zuwegungSeilzugflaeche: {
        //Algemein
        'fremdobjekte-auf-flaeche-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.allgemein.fremdobjekte-auf-flaeche-bewertung')?.['eingabeAuswahlWert'],
        //Gelände
        'beschaffenheit-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.beschaffenheit-group.beschaffenheit-bewertung')?.['eingabeAuswahlWert'],
        'gefaelle-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.gefaelle-group.gefaelle-bewertung')?.['eingabeAuswahlWert'],
        'ausholzung-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.ausholzung-group.ausholzung-bewertung')?.['eingabeAuswahlWert'],
        'rodung-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.rodung-group.rodung-bewertung')?.['eingabeAuswahlWert'],
        'zuwegung-breite-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.zuwegung.zuwegung-breite-bewertung')?.['eingabeAuswahlWert'],
        'gelaende-sonstiges-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.sonstiges-group.sonstiges-bewertung')?.['eingabeAuswahlWert'],
        'graben-bewertung': getGrabenquerungen(ba, fragenblock)
        .map(item => item['graben-bewertung']) // Extrahiert die Bewertungen
        .filter(Boolean), // Entfernt leere Werte
        //Ökologie
        'biotoptypkartierung-bewertung':fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.biotoptypkartierung-bewertung')?.['eingabeAuswahlWert'],
        'einzelbaeume-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.einzelbaeume-bewertung')?.['eingabeAuswahlWert'],
        'archaeologisches-denkmal-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.archaeologisches-denkmal-bewertung')?.['eingabeAuswahlWert'],
        'ffh-gebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.ffh-gebiet-bewertung')?.['eingabeAuswahlWert'],
        'vogelschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.vogelschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'geotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.geotop-bewertung')?.['eingabeAuswahlWert'],
        'geschuetztes-offenlandbiotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.geschuetztes-offenlandbiotop-bewertung')?.['eingabeAuswahlWert'],
        'geschuetztes-waldbiotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.geschuetztes-waldbiotop-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmal-flaechenhaft-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.naturdenkmal-flaechenhaft-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmal-einzelgebilde-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.naturdenkmal-einzelgebilde-bewertung')?.['eingabeAuswahlWert'],
        'ueberschwemmungsgebiet-hq100-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.ueberschwemmungsgebiet-hq100-bewertung')?.['eingabeAuswahlWert'],
        'ueberschwemmungsgebiet-m1-m2-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.ueberschwemmungsgebiet-m1-m2-bewertung')?.['eingabeAuswahlWert'],
        'gewaessernetz-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.gewaessernetz-bewertung')?.['eingabeAuswahlWert'],
        'wasserschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.wasserschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'heilquellenschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.heilquellenschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'landschaftsschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.landschaftsschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'waldschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.waldschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'naturschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.naturschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'biosphaerengebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.biosphaerengebiet-bewertung')?.['eingabeAuswahlWert'],
        'bodendenkmale-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.bodendenkmale-bewertung')?.['eingabeAuswahlWert'],
        'hoehlenbaeume-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.hoehlenbaeume-bewertung')?.['eingabeAuswahlWert'],
        'haselmausnester-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.haselmausnester-bewertung')?.['eingabeAuswahlWert'],
        'reptilien-bewertung': fragenblock.findFrageByRelativePath(ba, 'zuwegung-seilzugflaeche-modal.oekologie-group.reptilien-bewertung')?.['eingabeAuswahlWert'],
      },
      flaecheZwischenMastUndSeilzug: {
        // Allgemein
        'nutzungsart-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.allgemein.nutzungsart-bewertung')?.['eingabeAuswahlWert'],
        'fremdobjekte-auf-flaeche-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.allgemein.fremdobjekte-auf-flaeche-bewertung')?.['eingabeAuswahlWert'],
        // Gelände
        'ausholzung-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.ausholzung-group.ausholzung-bewertung')?.['eingabeAuswahlWert'],
        'rodung-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.rodung-group.rodung-bewertung')?.['eingabeAuswahlWert'],
        'feuchtgebiete-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.feuchtgebiete-group.feuchtgebiete-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmaeler-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.naturdenkmaeler-group.naturdenkmaeler-bewertung')?.['eingabeAuswahlWert'],
        'kampfmittel-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.kampfmittel-group.kampfmittel-bewertung')?.['eingabeAuswahlWert'],
        'sonstiges-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.sonstiges-group.sonstiges-bewertung')?.['eingabeAuswahlWert'],
        // Ökologie

        'biotoptypkartierung-bewertung':fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.biotoptypkartierung-bewertung')?.['eingabeAuswahlWert'],
        'einzelbaeume-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.einzelbaeume-bewertung')?.['eingabeAuswahlWert'],
        'archaeologisches-denkmal-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.archaeologisches-denkmal-bewertung')?.['eingabeAuswahlWert'],
        'ffh-gebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.ffh-gebiet-bewertung')?.['eingabeAuswahlWert'],
        'vogelschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.vogelschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'geotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.geotop-bewertung')?.['eingabeAuswahlWert'],
        'geschuetztes-offenlandbiotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.geschuetztes-offenlandbiotop-bewertung')?.['eingabeAuswahlWert'],
        'geschuetztes-waldbiotop-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.geschuetztes-waldbiotop-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmal-flaechenhaft-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.naturdenkmal-flaechenhaft-bewertung')?.['eingabeAuswahlWert'],
        'naturdenkmal-einzelgebilde-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.naturdenkmal-einzelgebilde-bewertung')?.['eingabeAuswahlWert'],
        'ueberschwemmungsgebiet-hq100-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.ueberschwemmungsgebiet-hq100-bewertung')?.['eingabeAuswahlWert'],
        'ueberschwemmungsgebiet-m1-m2-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.ueberschwemmungsgebiet-m1-m2-bewertung')?.['eingabeAuswahlWert'],
        'gewaessernetz-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.gewaessernetz-bewertung')?.['eingabeAuswahlWert'],
        'wasserschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.wasserschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'heilquellenschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.heilquellenschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'landschaftsschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.landschaftsschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'waldschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.waldschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'naturschutzgebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.naturschutzgebiet-bewertung')?.['eingabeAuswahlWert'],
        'biosphaerengebiet-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.biosphaerengebiet-bewertung')?.['eingabeAuswahlWert'],
        'bodendenkmale-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.bodendenkmale-bewertung')?.['eingabeAuswahlWert'],
        'hoehlenbaeume-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.hoehlenbaeume-bewertung')?.['eingabeAuswahlWert'],
        'haselmausnester-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.haselmausnester-bewertung')?.['eingabeAuswahlWert'],
        'reptilien-bewertung': fragenblock.findFrageByRelativePath(ba, 'flaeche-zwischen-mast-und-seilzugflaeche-modal.oekologie-group.reptilien-bewertung')?.['eingabeAuswahlWert'],
      },
    };
  };
  const summarizeFremdleitungenBewertung = (ba: Bestandsaufnahme) => {
    const bewertungen: Array<string | undefined> = Object.values(
      getFremdleitungenBewertungen(ba)
    ).flat(1);
    return summarize(bewertungen);
  };
  const summarizeZuwegungBewertung = (ba: Bestandsaufnahme) => {
    const bewertungen: Array<string | undefined> = Object.values(
      getZuwegungBewertungen(ba)
    ).flat(1);
    return summarize(bewertungen);
  };
  const summarizeArbeitsFlaechenBewertung = (ba: Bestandsaufnahme) => {
    const bewertungen: Array<string | undefined> = Object.values(
      getArbeitsFlaecheBewertungen(ba)
    )
      .flat(1)
      .map((f) => Object.values(f))
      .flat(1);
    return summarize(bewertungen);
  };
  const summarizeOverallSeilzugflaechen = (ba: Bestandsaufnahme) => {
    const seilzugflaechen = getSeilzugflaechen(ba);
    let seilzugflaechenBewertung: any[] = [];
    seilzugflaechen?.forEach((seilzugflaeche, idx) => {
      const bewertung = Object.values(getSeilzugflaecheBewertungen(ba, seilzugflaeche)
      )
        .flat(1)
        .map((f) => Object.values(f))
        .flat(1);
      seilzugflaechenBewertung = seilzugflaechenBewertung.concat(bewertung);

      const bewertungenGraben: any = Object.values(
        getGrabenquerungenBewertungen(ba, seilzugflaeche) as any
      )
        .flat(1)
        .map((f) => Object.values(f as Record<string, unknown>))
        .flat(1);

      seilzugflaechenBewertung = seilzugflaechenBewertung.concat(bewertungenGraben);
    });



    const a = seilzugflaechenBewertung.concat(
    );

    return a;
  };
  const summarizeOverall = (ba: Bestandsaufnahme) => {
    const bewertungenArbeitsflaechen: Array<string | undefined> = Object.values(
      getArbeitsFlaecheBewertungen(ba)
    )
      .flat(1)
      .map((f) => Object.values(f))
      .flat(1);
    const bewertungenSeilzugflaechen: Array<string | undefined> = Object.values(
      summarizeOverallSeilzugflaechen(ba)
      );
    const bewertungenZuwegung: Array<string | undefined> = Object.values(
      getZuwegungBewertungen(ba)
    ).flat(1);
    const bewertungenFremdleitungen: Array<string | undefined> = Object.values(
      getFremdleitungenBewertungen(ba)
    ).flat(1);
    const bewertungenSchutzgerueste: Array<string | undefined> = Object.values(
      getSchutzgeruesteBewertungen(ba)
    ).flat(1);
    const a = bewertungenArbeitsflaechen.concat(
      bewertungenZuwegung,
      bewertungenFremdleitungen,
      bewertungenSeilzugflaechen,
      bewertungenSchutzgerueste
    );
    return summarize(a);
  };
  const extractModalsFrom1NModals = (
    ba: Bestandsaufnahme,
    modalIdentifier: string
  ) => {
    const modal = useIdentifierFragenblockFinder(ba, modalIdentifier);

    const modale = modal?.fragenblocks?.filter(
      (block) => !block.config.isTemplate
    );

    return modale
      ?.map((modal) => {
        return modal.fragenblocks.map((fragenBlock) => {
          return useIdentifierFrageFinder(ba, fragenBlock.path);
        });
      })
      .flat(1);
  };
  const getAnmerkungenGutachter = (ba: Bestandsaufnahme) => {
    const anmerkungenModals = useIdentifierFragenblockFinder(
      ba,
      'tng-be-flaechen.anmerkung-des-gutachters-group'
    )?.['fragenblocks'];
  
    const res = anmerkungenModals?.map((modal: any) => {
      const elems = modal?.frages
        ?.map((f: any) => {
          if (f.eingabeTyp === 'Text' && f.identifier === 'custom-title') {
            return { title: f.eingabeText };
          } else if (f.identifier === 'custom-freitext') {
            return { anmerkung: f.eingabeText };
          } else if (f.identifier === 'custom-bilder') {
            return { bilder: f.eingabeMedien };
          }
          return null;
        })
        .filter(Boolean);
      return Object.assign({}, ...elems);
    });
  
    return res;
  };
  const getAnmerkungenPages = (ba: Bestandsaufnahme) => {
    const res = getAnmerkungenGutachter(ba);

    const maxSpace = 100;
    const spaceForSingleModal = 30;
    let currentOccupiedSpace = 0;

    const pages: any[] = [];
    let currentPage: any = {
      'anmerkungen': []
    };

    res?.forEach(anmerkung => {
      if(currentOccupiedSpace + spaceForSingleModal < maxSpace){
        currentPage['anmerkungen'].push(anmerkung);
        currentOccupiedSpace += spaceForSingleModal;
      }else{
        pages.push(currentPage);
        currentOccupiedSpace = 0;
        currentPage = {
          'anmerkungen': []
        };
        currentPage['anmerkungen'].push(anmerkung);
        currentOccupiedSpace += spaceForSingleModal;
      }
    });
    pages.push(currentPage);
    return pages;
  }
  const getGraben = (ba: Bestandsaufnahme) => {
    const grabenModals = extractModalsFrom1NModals(
      ba,
      'tng-be-flaechen.zuwegung-mast-tab.graben'
    );
    const res = grabenModals?.map((modal: any) => {
      const elems = modal?.frages?.map((f: any) => {
        if (f.identifier === 'art') {
          return { 'art': f.eingabeAuswahlWert };
        } else if (f.identifier === 'abstand') {
          return { 'abstand': f.eingabeText };
        } else if (f.identifier === 'graben-beschreibung') {
          return { 'graben-beschreibung': f.eingabeText };
        } else if (f.identifier === 'graben-bewertung') {
          return { 'graben-bewertung': f.eingabeAuswahlWert };
        } else if (f.identifier === 'graben-bilder') {
          return { 'graben-bilder': f.eingabeMedien };
        }
      });
      return Object.assign(elems[0], elems[1], elems[2], elems[3], elems[4]);
    } );
    return res;
  };
  const getGrabenquerungen = (ba: Bestandsaufnahme, fragenblock: Fragenblock) => {
    const grabenModals = fragenblock.findFragenblockByRelativePath(
      ba,
      "zuwegung-seilzugflaeche-modal.grabenquerung-group"
    )?.fragenblocks;  
    if (!grabenModals || !Array.isArray(grabenModals)) {
      return [];
    }
    return grabenModals.map((modal: any) => {
      const fragenblock = modal?.fragenblocks?.[0];
      const result: any = {};
      fragenblock.frages.forEach((f: any) => {
        switch (f.identifier) {
          case "grabenquerung-erforderlich":
            result["graben-erforderlich"] = f.eingabeAuswahlWert;
            break;
          case "graben-anzahl":
            result["graben-anzahl"] = f.eingabeText;
            break;
          case "graben-beschreibung":
            result["graben-beschreibung"] = f.eingabeText;
            break;
          case "graben-bewertung":
            result["graben-bewertung"] = f.eingabeAuswahlWert;
            break;
          case "graben-bilder":
            result["graben-bilder"] = f.eingabeMedien;
            break;
        }
      });
      return result;
    }).filter(Boolean); // Entfernt `null`-Werte aus dem finalen Array
  };
  const getGrabenPages = (ba: Bestandsaufnahme) => {
    const res = getGraben(ba);

    const maxSpace = 100;
    const spaceForSingleModal = 30;
    let currentOccupiedSpace = 0;

    const pages: any[] = [];
    let currentPage: any = {
      'grabenquerungen': []
    };

    res?.forEach(grabenquerung => {
      if(currentOccupiedSpace + spaceForSingleModal < maxSpace){
        currentPage['grabenquerungen'].push(grabenquerung);
        currentOccupiedSpace += spaceForSingleModal;
      }else{
        pages.push(currentPage);
        currentOccupiedSpace = 0;
        currentPage = {
          'grabenquerungen': []
        };
        currentPage['grabenquerungen'].push(grabenquerung);
        currentOccupiedSpace += spaceForSingleModal;
      }
    });
    pages.push(currentPage);
    return pages;
  }
  const getBauwerke = (ba: Bestandsaufnahme) => {
    const grabenModals = extractModalsFrom1NModals(
      ba,
      'tng-be-flaechen.zuwegung-mast-tab.bauwerke'
    );
    const res = grabenModals?.map((modal: any) => {
      const elems = modal?.frages?.map((f: any) => {
        if (f?.identifier === 'freitext') {
          return { freitext: f?.eingabeText };
        } else if (f?.identifier === 'bauwerk-beschreibung') {
          return { beschreibung: f?.eingabeText };
        } else if (f?.identifier === 'tragfaehig') {
          return { tragfaehig: f?.eingabeAuswahlWert };
        } else if (f?.identifier === 'bauwerk-bewertung') {
          return { 'bauwerk-bewertung': f?.eingabeAuswahlWert };
        } else if (f?.identifier === 'bild') {
          return { bild: f?.eingabeMedien };
        }
      });
      return Object.assign(elems[0], elems[1], elems[2], elems[3], elems[4]);
    });
    return res;
  };
  const getBauwerkePages = (ba: Bestandsaufnahme) => {
    const res = getBauwerke(ba);

    const maxSpace = 100;
    const spaceForSingleModal = 9;
    let currentOccupiedSpace = 0;

    const pages: any[] = [];
    let currentPage: any = {
      'bauwerke': []
    };

    res?.forEach(bauwerk => {
      if(currentOccupiedSpace + spaceForSingleModal < maxSpace){
        currentPage['bauwerke'].push(bauwerk);
        currentOccupiedSpace += spaceForSingleModal;
      }else{
        pages.push(currentPage);
        currentOccupiedSpace = 0;
        currentPage = {
          'bauwerke': []
        };
        currentPage['bauwerke'].push(bauwerk);
        currentOccupiedSpace += spaceForSingleModal;
      }
    });
    pages.push(currentPage);
    return pages;
  }
  const getFremdleitungen = (ba: Bestandsaufnahme) => {
    const grabenModals = extractModalsFrom1NModals(
      ba,
      'tng-be-flaechen.fremdleitungen-tab.fremdleitungen-group'
    );
    const res = grabenModals?.map((modal: any) => {
      const elems = modal?.frages?.map( ( f: any ) => {
        if (f.identifier === 'typ') {
          return { typ: f.eingabeAuswahlWert };
        } else if (f.identifier === 'art-oberirdisch') {
          return { 'art-oberirdisch': f.eingabeAuswahlWert };
        } else if (f.identifier === 'art-unterirdisch') {
          return { 'art-unterirdisch': f.eingabeAuswahlWert };
        } else if ( f.identifier === 'betroffeneflaechen' ) {
          return { betroffeneflaechen: f.eingabeText };
        } else if ( f.identifier === 'betreiber-bekannt' ) {
          return { 'betreiber-bekannt': f.eingabeBoolean };
        } else if (f.identifier === 'auflagen') {
          return { 'auflagen': f.eingabeAuswahlWert };
        } else if ( f.identifier === 'betreiber' ) {
          return { 'betreiber': f.eingabeText };
        } else if ( f.identifier === 'fremdleitung-bewertung' ) {
          return { 'fremdleitung-bewertung': f.eingabeAuswahlWert };
        }
      } );

      return Object.assign(
        elems[0],
        elems[1],
        elems[2],
        elems[3],
        elems[4],
        elems[5],
        elems[6],
        elems[7],
        elems[8]
      );
    } );
    return res;
  }
  const getFremdleitungenPages = (ba: Bestandsaufnahme) => {
    const res = getFremdleitungen(ba);

    const maxSpace = 100;
    const spaceForSingleModal = 10;
    let currentOccupiedSpace = 0;

    const pages: any[] = [];
    let currentPage: any = {
      'fremdleitungen': []
    };

    res?.forEach(fremdleitung => {

      if(currentOccupiedSpace + spaceForSingleModal < maxSpace){
        currentPage['fremdleitungen'].push(fremdleitung);
        currentOccupiedSpace += spaceForSingleModal;
      }else{
        pages.push(currentPage);
        currentOccupiedSpace = 0;
        currentPage = {
          'fremdleitungen': []
        };
        currentPage['fremdleitungen'].push(fremdleitung);
        currentOccupiedSpace += spaceForSingleModal;
      }
    });
    pages.push(currentPage);

    return pages;
  }
  const getSchutzgerueste = (ba: Bestandsaufnahme) => {
    const schutzgeruesteTab = ba?.findFragenblockByRelativePath('schutzgerueste-tab');
    const schutzgerueste = schutzgeruesteTab?.getMultipleFragenblockInstances('schutzgerueste');
    return schutzgerueste;
  }
  const getSchutzgeruestePages = (ba: Bestandsaufnahme) => {
    const res = getFremdleitungen(ba);

    const maxSpace = 100;
    const spaceForSingleModal = 10;
    let currentOccupiedSpace = 0;

    const pages: any[] = [];
    let currentPage: any = {
      'schutzgerueste': []
    };

    res?.forEach(fremdleitung => {

      if(currentOccupiedSpace + spaceForSingleModal < maxSpace){
        currentPage['schutzgerueste'].push(fremdleitung);
        currentOccupiedSpace += spaceForSingleModal;
      }else{
        pages.push(currentPage);
        currentOccupiedSpace = 0;
        currentPage = {
          'schutzgerueste': []
        };
        currentPage['schutzgerueste'].push(fremdleitung);
        currentOccupiedSpace += spaceForSingleModal;
      }
    });
    pages.push(currentPage);

    return pages;
  }
  const extractModalsFrom1NModalsMehrfach = (
    ba: Bestandsaufnahme,
    fragenblock: Fragenblock,
    modalIdentifier: string,
  ) => {
    // const modal = useIdentifierFragenblockFinder(ba, modalIdentifier);
    const modal = fragenblock?.findFragenblockByRelativePath(ba, modalIdentifier);
    const modale = modal?.fragenblocks?.filter(
      (block) => !block.config.isTemplate
    );

    return modale
      ?.map((modal) => {
        return modal.fragenblocks.map((fragenBlock) => {
          return useIdentifierFrageFinder(ba, fragenBlock.path);
        });
      })
      .flat(1);
  };
  const getGrabenquerungenBewertungen = (ba: Bestandsaufnahme, fragenblock: Fragenblock) => {
    const grabenModals = extractModalsFrom1NModalsMehrfach(
      ba,
      fragenblock,
      'zuwegung-seilzugflaeche-modal.grabenquerung-group'
    );

    const res = grabenModals?.map((modal: any) => {
      const elems = modal?.frages?.map((f: any) => {
        if (f.identifier === 'graben-bewertung') {
          return { 'graben-bewertung': f.eingabeAuswahlWert };
        }
      });
      return Object.assign(elems[4]);
    });
    return res;
  }
  const getSeilzugflaechen = (ba: Bestandsaufnahme) => {
    const seilzugflaechenTab = ba?.findFragenblockByRelativePath('seilzugflaeche-tab');
    const seilzugflaechen = seilzugflaechenTab?.getMultipleFragenblockInstances('seilzugflaeche');
    return seilzugflaechen;
  }
  const getSeilzugflaechenJSON = (ba: Bestandsaufnahme) => {

    const seilzugflaechen = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, 'rippistein-seilzugflaechen.seilzugflaeche-tab')?.fragenblocks));
    seilzugflaechen?.shift();
    return seilzugflaechen;
  }
  const getSeilzugflaechenWrappers = (ba: Bestandsaufnahme) => {
    const seilzugflaechen: any[] = getSeilzugflaechenJSON(ba)!;
    const retVal: any = [];

    //wip fko start
    const seilzugflaechenTab = ba?.findFragenblockByRelativePath('seilzugflaeche-tab');
    const seilzugflaechen_raw = seilzugflaechenTab?.getMultipleFragenblockInstances('seilzugflaeche');

    seilzugflaechen_raw?.forEach(seilzugflaeche_raw => {
      const entfernungMastBewertung = ba && seilzugflaeche_raw.findFrageByRelativePath(ba, 'seilzugflaeche-modal.allgemein.entfernung-mast-bewertung')?.getCurrentInput();
    });
    // wip end fko
    seilzugflaechen?.forEach(seilzugflaeche => {

      //Object consisting of SZF Pages[] and Grabenquerungen (these consist of Grabenquerungen Pages[])
      const wrappedSZF = {
        'seilzugflaechenPages': <any>undefined,
        'grabenquerungenPages': <any>undefined,
        'ergebnisSeilzugflaeche': <any>undefined,
        'ergebnisZuwegung': <any>undefined
      };

      wrappedSZF['seilzugflaechenPages'] = [];

      // Allgemeine info page
      const firstPage = {
        'typ': <any>undefined,
        'mastnummer': <any>undefined,
        'betroffeneflurstuecke': <any>undefined,
        'mastbild': <any>undefined,
        'masthoehe': <any>undefined,
        'flaeche-vorhanden': <any>undefined,
        'flaeche-notwendig': <any>undefined
      };
      // Seilzugflaeche Page 1
      const secondPage = {
        'entfernung-mast-soll': <any>undefined,
        'entfernung-mast-ist': <any>undefined,
        'entfernung-mast-bewertung': <any>undefined,
        'betroffeneflurstuecke': <any>undefined,
        'allgemein.nutzungsart-der-flaeche': <any>undefined,
        'temptodothing': <any>undefined,
        'nutzungsart-bewertung': <any>undefined,
        'fremdobjekte-vorhanden': <any>undefined,
        'fremdobjekte-auf-flaeche-bewertung': <any>undefined,
        'sonstiges-vorhanden': <any>undefined,
        'sonstiges-bewertung': <any>undefined
      };

      // --------------------------------------------------------- Allgemein --------------------------------------------------------------------------------------
      const allgemein = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.allgemein-modal')?.fragenblocks));
      allgemein[0]?.frages?.forEach((frage: any) => {
        switch (frage.identifier) {
          case 'typ':
            firstPage['typ'] = frage.eingabeAuswahlWert; //TODO change to label
            break;
          case 'mastnummer':
            firstPage['mastnummer'] = frage.eingabeText;
            break;
          case 'betroffeneflurstuecke':
            firstPage['betroffeneflurstuecke'] = frage.eingabeText;
            break;
          case 'mastbild':
            firstPage['mastbild'] = frage.eingabeAuswahlWert; //TODO change to label;
            break;
          case 'masthoehe':
            firstPage['masthoehe'] = frage.eingabeText;
            break;
          case 'flaeche-vorhanden':
            firstPage['flaeche-vorhanden'] = frage.eingabeBoolean;
            break;
          case 'flaeche-notwendig':
            firstPage['flaeche-notwendig'] = frage.eingabeBoolean;
            break;
          default:
            break;
        }
      });

      // --------------------------------------------------------- Seilzugfläche ----------------------------------------------------------------------------------
      const seilzugflaecheFb = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.seilzugflaeche-modal')?.fragenblocks));


      const thirdPage = {
        'gefaelle-vorhanden': <any>undefined,
        'gefaelle-bewertung': <any>undefined
      };

      const szfAllgemein = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.seilzugflaeche-modal.allgemein')));

      szfAllgemein?.frages?.forEach((frage: any) => {
        switch (frage.identifier) {
          case 'entfernung-mast-soll':
            secondPage['entfernung-mast-soll'] = frage.eingabeText;
            break;
          case 'entfernung-mast-ist':
            secondPage['entfernung-mast-ist'] = frage.eingabeText;
            break;
          case 'entfernung-mast-bewertung':
            secondPage['entfernung-mast-bewertung'] = frage.eingabeAuswahlWert;
            break;
          case 'betroffeneflurstuecke':
            secondPage['betroffeneflurstuecke'] = frage.eingabeText;
            break;
          case 'allgemein.nutzungsart-der-flaeche':
            secondPage['allgemein.nutzungsart-der-flaeche'] = frage.eingabeAuswahlWerts;
            break;
          case 'nutzungsart-bewertung':
            secondPage['nutzungsart-bewertung'] = frage.eingabeAuswahlWert;
            break;
          case 'fremdobjekte-vorhanden':
            secondPage['fremdobjekte-vorhanden'] = frage.eingabeBoolean;
            break;
          case 'fremdobjekte-auf-flaeche-bewertung':
            secondPage['fremdobjekte-auf-flaeche-bewertung'] = frage.eingabeAuswahlWert;
            break;
          case 'sonstiges-vorhanden':
            secondPage['sonstiges-vorhanden'] = frage.eingabeBoolean;
            break;
          case 'sonstiges-bewertung':
            secondPage['sonstiges-bewertung'] = frage.eingabeAuswahlWert;
            break;

          default:
            break;
        }
      });

      const szfGefaelleGroup = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.seilzugflaeche-modal.gefaelle-group')));
      szfGefaelleGroup?.frages?.forEach((frage: any) => {
        switch (frage.identifier) {
          case 'gefaelle-vorhanden':
            thirdPage['gefaelle-vorhanden'] = frage.eingabeBoolean;
            break;
          case 'gefaelle-bewertung':
            thirdPage['gefaelle-bewertung'] = frage.eingabeAuswahlWert;
            break;

          default:
            break;
        }
      });


      // --------------------------------------------------------- Zuwegung Seilzugfläche -------------------------------------------------------------------------
      const zuwegungSeilzugflaeche = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.zuwegung-seilzugflaeche-modal')?.fragenblocks));

      const fourthPage = {
        'betroffeneflurstuecke': <any>undefined,
        'fremdobjekte-vorhanden': <any>undefined,
        'fremdobjekte-auf-flaeche': <any>undefined,
        'weitere-fremdobjekte-auf-flaeche': <any>undefined,
        'fremdobjekte-auf-flaeche-bewertung': <any>undefined,
        'sonstiges-vorhanden': <any>undefined,
        'sonstiges-beschreibung': <any>undefined,
        'sonstiges-bewertung': <any>undefined,

        'beschaffenheit': <any>undefined,
        'beschaffenheit-bewertung': <any>undefined,

        'gefaelle-vorhanden': <any>undefined,
        'gefaelle-bewertung': <any>undefined,
        'gefaelle-bilder': <any>undefined,

        /*

        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,

        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,

        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,

        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,

        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,
        'todotodo': <any>undefined,

        'todotodo': <any>undefined
        */

      };

      const zuwegungAllgemein = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.zuwegung-seilzugflaeche-modal.allgemein')));

      zuwegungAllgemein?.frages?.forEach((frage: any) => {
        switch (frage.identifier) {
          case 'betroffeneflurstuecke':
            fourthPage['betroffeneflurstuecke'] = frage.eingabeText;
            break;
          case 'fremdobjekte-vorhanden':
            fourthPage['fremdobjekte-vorhanden'] = frage.eingabeBoolean;
            break;
          case 'fremdobjekte-auf-flaeche':
            fourthPage['fremdobjekte-auf-flaeche'] = frage.eingabeText;
            break;
          case 'weitere-fremdobjekte-auf-flaeche':
            fourthPage['weitere-fremdobjekte-auf-flaeche'] = frage.eingabeText;
            break;
          case 'fremdobjekte-auf-flaeche-bewertung':
            fourthPage['fremdobjekte-auf-flaeche-bewertung'] = frage.eingabeAuswahlWert;
            break;
          case 'sonstiges-vorhanden':
            fourthPage['sonstiges-vorhanden'] = frage.eingabeBoolean;
            break;
          case 'sonstiges-beschreibung':
            fourthPage['sonstiges-beschreibung'] = frage.eingabeText;
            break;
          case 'sonstiges-bewertung':
            fourthPage['sonstiges-bewertung'] = frage.eingabeAuswahlWert;
            break;

          default:
            break;
        }
      });

      const zuewgungBeschaffenheit = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.zuwegung-seilzugflaeche-modal.beschaffenheit-group')));

      zuewgungBeschaffenheit?.frages?.forEach((frage: any) => {
        switch (frage.identifier) {
          case 'beschaffenheit':
            fourthPage['beschaffenheit'] = frage.eingabeText;
            break;
          case 'beschaffenheit-bewertung':
            fourthPage['beschaffenheit-bewertung'] = frage.eingabeBoolean;
            break;

          default:
            break;
        }
      });

      const zuewgungGefaelle = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.zuwegung-seilzugflaeche-modal.gefaelle-group')));

      zuewgungGefaelle?.frages?.forEach((frage: any) => {
        switch (frage.identifier) {
          case 'gefaelle-vorhanden':
            fourthPage['gefaelle-vorhanden'] = frage.eingabeBoolean;
            break;
          case 'gefaelle-bewertung':
            fourthPage['gefaelle-bewertung'] = frage.eingabeAuswahlWert;
            break;
          case 'gefaelle-bilder':
            fourthPage['gefaelle-bilder'] = frage.eingabeMedien;
            break;

          default:
            break;
        }
      });

      // --------------------------------------------------------- Fläche zwischen Mast und Seilzugfläche ---------------------------------------------------------
      const flaecheZwischenMastUndSeilzugflaeche = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.flaeche-zwischen-mast-und-seilzugflaeche-modal')?.fragenblocks));
      // --------------------------------------------------------- Ergebnis für Seilzugfläche ---------------------------------------------------------------------
      const ergebnis = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, seilzugflaeche.path + '.ergebnis-modal')?.fragenblocks));


      wrappedSZF['seilzugflaechenPages'].push(firstPage);
      wrappedSZF['seilzugflaechenPages'].push(secondPage);
      wrappedSZF['seilzugflaechenPages'].push(thirdPage);
      wrappedSZF['seilzugflaechenPages'].push(fourthPage);


      // --------------------------------------------------------- Grabenquerungen --------------------------------------------------------------------------------

      let zuwegungPath = zuwegungSeilzugflaeche[0].path;
      zuwegungPath = zuwegungPath.substring(0, zuwegungPath.lastIndexOf('.'));
      const grabenquerungenPath = zuwegungPath + '.grabenquerung-group';

      const grabenQuerungenFbs = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, grabenquerungenPath))).fragenblocks;
      grabenQuerungenFbs.shift();

      const grabenquerungenPages: any[] = [];

      let currentPage = {
        'grabenquerungen': <any>[]
      };
      const maxSpace = 100;
      const modalSize = 45;
      let usedSpace = 0;

      grabenQuerungenFbs.forEach((grabenQuerungFb: Fragenblock) => {
        const grabenQuerung = {
          'erforderlich': <any>undefined,
          'anzahl': <any>undefined,
          'beschreibung': <any>undefined,
          'bilder': <any>undefined,
          'bewertung': <any>undefined
        };

        const fragenContainer = JSON.parse(JSON.stringify(useIdentifierFragenblockFinder(ba, grabenQuerungFb.path + '.grabenquerung')));
        fragenContainer?.frages?.forEach((frage: any) => {
          switch (frage.identifier) {
            case 'grabenquerung-erforderlich':
              grabenQuerung['erforderlich'] = frage.eingabeAuswahlWert; //TODO change to label
              break;
            case 'graben-anzahl':
              grabenQuerung['anzahl'] = frage.eingabeText;
              break;
            case 'graben-beschreibung':
              grabenQuerung['beschreibung'] = frage.eingabeText;
              break;
            case 'graben-bilder':
              grabenQuerung['bilder'] = frage.eingabeMedien;
              break;
            case 'graben-bewertung':
              grabenQuerung['bewertung'] = frage.eingabeAuswahlWert; //TODO change to label
              break;
            default:
              break;
          }
        });

        if (usedSpace + modalSize < maxSpace) {

          currentPage['grabenquerungen'].push(grabenQuerung);
          usedSpace += modalSize;

        } else {
          grabenquerungenPages.push(currentPage);
          currentPage = {
            'grabenquerungen': <any>[]
          };

          currentPage['grabenquerungen'].push(grabenQuerung);
          usedSpace = modalSize;

        }

      });
      grabenquerungenPages.push(currentPage);


      wrappedSZF['grabenquerungenPages'] = grabenquerungenPages;

      wrappedSZF['ergebnisSeilzugflaeche'] = "Test";
      wrappedSZF['ergebnisZuwegung'] = "Test";

      retVal.push(wrappedSZF);
    });


    return retVal;
  }

  return {
    getZuwegungBewertungen,
    getArbeitsFlaecheBewertungen,
    getFremdleitungenBewertungen,
    summarizeFremdleitungenBewertung,
    summarizeZuwegungBewertung,
    summarizeArbeitsFlaechenBewertung,
    summarizeOverall,
    getAnmerkungenGutachter,
    getAnmerkungenPages,
    getGrabenquerungen,
    getGraben,
    getGrabenPages,
    getBauwerke,
    getBauwerkePages,
    getFremdleitungen,
    getFremdleitungenPages,
    getSeilzugflaecheBewertungen,
    getSchutzgeruesteBewertungen,
    summarizeSchutzgeruesteBewertung,
    summarizeSeilzugflaecheBewertungen,
    summarizeOverallSeilzugflaechen,
    getSeilzugflaechenWrappers,
    getSeilzugflaechen,
    getSchutzgerueste,
    getSchutzgeruestBewertungen,
    getSchutzgeruestePages
  };
}
